/* eslint-disable sort-keys */
/**
 * Colors
 */

import { css } from '@emotion/core';
import type { SerializedStyles } from '@emotion/core';

// Theme colors
export const PRIMARY_COLOR = '#0070ba';
export const PRIMARY_COLOR_V2 = '#0070E0';
export const ERROR_COLOR = '#d20000';
export const ERROR_COLOR_V3 = '#C40B0B';
export const ERROR_COLOR_V2 = '#FFD2D2';
export const SOLD_OUT_TAG_COLOR = '#c72e2e';
export const SUCCESS_COLOR = '#00cf92';
export const SUCCESS_COLOR_V2 = '#0F7013';
export const WARNING_COLOR = '#ff9600';
export const WARNING_COLOR_V2 = '#E6E0D9';
export const YELLOW_BACKGROUND_COLOR = '#FFE888';
export const WHITE_COLOR = '#ffffff';
export const BLACK_COLOR = '#222222';
export const HIGH_CONTRAST_TEXT_COLOR = '#001435';

// 4.1 Rebrand colors
// Sourced from https://www.figma.com/design/rqIQqkpFEzjkb3AMclY0I8/Purchase%E2%80%A8%E2%80%93-5.1-E2E-Funnel?node-id=1199-8715&m=dev
export const REBRAND_T1_PRIMARY_COLOR = '#000000';
export const REBRAND_T1_SELECTION_COLOR = '#0066f5';
export const REBRAND_T1_HOVER_COLOR = '#333333';
export const REBRAND_T1_ACTIVE_COLOR = '#696969';
export const REBRAND_T1_TEXT_COLOR = '#FFFFFF';
export const REBRAND_T1_PRIMARY_INPUT_BORDER = '#999999';
export const REBRAND_T1_PRIMARY_T1_HOVER_SWITCH = '#cccccc';

export const REBRAND_T2_PRIMARY_COLOR = '#60CDFF';
export const REBRAND_T2_SELECTION_COLOR = '#0066f5';
export const REBRAND_T2_HOVER_COLOR = '#008CFF';
export const REBRAND_T2_ACTIVE_COLOR = '#E3F7FF';
export const REBRAND_T2_TEXT_COLOR = '#000000';

export const REBRAND_T3_PRIMARY_COLOR = '#002991';
export const REBRAND_T3_SELECTION_COLOR = '#0066f5';
export const REBRAND_T3_HOVER_COLOR = '#0038BA';
export const REBRAND_T3_ACTIVE_COLOR = '#0057D9';
export const REBRAND_T3_TEXT_COLOR = '#FFFFFF';

export const REBRAND_INPUT_BORDER = '#cccccc';
export const REBRAND_ERROR_COLOR = '#c31526';
export const REBRAND_BORDER_WIDTH = '0.0625rem';
export const REBRAND_ERROR_BORDER_WIDTH = '0.125rem';
export const ALERT_WARNING_BACKGROUND_COLOR = '#fff5e1';
export const ALERT_ERROR_BACKGROUND_COLOR = '#fbeaeb';
export const ALERT_SUCCESS_BACKGROUND_COLOR = '#e6faee';
export const ALERT_WARNING_BORDER_COLOR = '#785000';
export const ALERT_ERROR_BORDER_COLOR = '#770a0a';
export const ALERT_SUCCESS_BORDER_COLOR = '#005243';
export const ALERT_WARNING_ICON_COLOR = '#d08b00';
export const ALERT_ERROR_ICON_COLOR = '#c31526';
export const ALERT_SUCCESS_ICON_COLOR = '#008556';

/**
 * Normally we should would avoid using "!important" but
 * we don't have much choice here since we are overwriting
 * pp-react v6 and v7 styling.
 */
/* stylelint-disable declaration-no-important */
export const REBRAND_SELECTION_STYLES = css`
  border: none !important;
  outline: 0.125rem solid ${REBRAND_T1_SELECTION_COLOR} !important;
  box-shadow: 0 0 0 0.125rem #ffffff !important;
  outline-offset: 0.125rem !important;
`;

export const REBRAND_INPUT_CONTAINER_STYLES = (
  hasError: boolean = false,
): SerializedStyles => css`
  &:focus-within {
    ${REBRAND_SELECTION_STYLES}
    border-style: solid !important;
    border-radius: 4px !important;
    border-color: ${hasError
      ? REBRAND_ERROR_COLOR
      : REBRAND_INPUT_BORDER} !important;
    border-width: ${hasError
      ? REBRAND_ERROR_BORDER_WIDTH
      : REBRAND_BORDER_WIDTH} !important;
  }
  border-color: ${hasError
    ? REBRAND_ERROR_COLOR
    : REBRAND_INPUT_BORDER} !important;
  border-width: ${hasError ? REBRAND_ERROR_BORDER_WIDTH : REBRAND_BORDER_WIDTH};
`;

export const REBRAND_SELECT_CONTAINER_STYLES = (
  hasError: boolean = false,
): SerializedStyles => css`
  & > div {
    ${REBRAND_INPUT_CONTAINER_STYLES(hasError)}
    border-width: ${hasError
      ? REBRAND_ERROR_COLOR
      : REBRAND_INPUT_BORDER} !important;
  }
`;
/* stylelint-enable declaration-no-important */

// Misc colors
export const BORDER_FOCUSED_COLOR = '#009cde';
export const BUTTON_FOCUSED_COLOR = '#003087';
export const DEFAULT_BORDER_COLOR = '#b7bcbf';
export const INPUT_DEFAULT_COLOR = '#000';
export const INPUT_PP_REACT_FOCUSED_COLOR = '#1040c1';
export const LABEL_COLOR = '#545D68';
export const LANG_COUNTRY_DIVIDER_COLOR = '#d3d4d1';
export const NON_INPUT_BORDER_COLOR = '#cbd2d6';
export const PP_REACT_TOGGLE_HOVER_COLOR = '#5BBBFC';
export const TOGGLE_OFF_BORDER_COLOR = '#929496';

// Disabled field colors
export const DISABLED_BACKGROUND_COLOR = '#ebecee';
export const DISABLED_BORDER_COLOR = '#b7bcbf';

// Page message colors
export const ERROR_MESSAGE_BACKGROUND_COLOR = '#fff7f7';
export const INFO_MESSAGE_BACKGROUND_COLOR = '#f5f7fa';
export const INFO_MESSAGE_BACKGROUND_COLOR_V2 = '#faf8f5';
export const INFO_MESSAGE_BACKGROUND_COLOR_V3 = '#edfcf3';
export const SUCCESS_MESSAGE_BACKGROUND_COLOR = '#f5fdfb';
export const WARNING_MESSAGE_BACKGROUND_COLOR = '#fffbf5';

// Text colors
export const TEXT_COLOR = '#6c7378';
export const TEXT_COLOR_V2 = '#515354';
export const TEXT_BLACK = '#000000'; // Used to replicate older experience
export const HEADER_COLOR = '#2c2e2f';
export const LINK_COLOR = '#0070ba';
export const SUB_HEADING_COLOR = '#687274';
export const LIGHT_GRAY_COLOR = '#687173';
export const GREYS_BLACK_COLOR = '#0C0C0D';
export const ALERT_RESEND_ERROR_TEXT_COLOR = '#9B0021';
export const LINK_COLOR_CONTROL = 'rgba(0, 0, 0, 0.7)';
export const ADDRESS_NORMALIZED_TEXT_COLOR = '#ff9700';

// Incentives colors
export const INCENTIVES_YELLOW_COLOR = '#ffd140';
export const INCENTIVES_LILAC_COLOR = '#24247f';
export const INCENTIVES_BLUE_COLOR = '#142c8e';
export const INCENTIVES_GREEN_COLOR_V1 = '#549265';
export const INCENTIVES_GREEN_COLOR_V2 = '#299976';

//RGBA COLORS
export const LIGHT_BLUE_RGBA = 'rgba(209, 229, 249, 0.9)';

/************ THEME COLORS ***********/
// PP 3.0 COLORS
// BRAND PRIMARY MAIN COLORS
export const PP_PRIMARY_COLOR = '#003087';
export const PP_SECONDARY_COLOR = '#0070E0';
export const PP_TERTIARY_COLOR = '#FAF8F5';
export const PP_FEATURED_COLOR = '#FFD140';

// UTILITY COLORS
export const PP_UNSELECTED_COLOR = '#E6E0D9';
export const PP_SELECTED_COLOR = '#0070E0';
export const PP_SELECTED_RANGE_COLOR = '#B9EFFC';
export const PP_LINK_MAIN_COLOR = '#0070E0';
export const PP_LINK_ALT_COLOR = '#001435';
export const PP_FOCUS_BORDER_COLOR = '#097FF5';
export const PP_OVERLAY_COLOR = 'rgba(0, 0, 0, 0.55)';

// ACCENT COLORS
export const PP_ACCENT_ONE_COLOR = '#003087';
export const PP_ACCENT_TWO_COLOR = '#E02D50';
export const PP_ACCENT_THREE_COLOR = '#388C00';
export const PP_ACCENT_FOUR_COLOR = '#7252CC';

// NOTIFICATIONS COLORS
export const PP_ERROR_COLOR = '#C40B0B';
export const PP_WARNING_MAIN_COLOR = '#FFBE4A';
export const PP_WARNING_ALT_COLOR = '#E85E00';
export const PP_SUCCESS_COLOR = '#0F7013';
export const PP_SYSTEM_COLOR = '#001435';
export const PP_INFO_COLOR = '#E6E0D9';
export const PP_SPECIALTY_COLOR = '#7252CC';

// STRUCTURE COLORS
export const PP_ACTIVE_BG_COLOR = '#001C64';
export const PP_BORDER_HIGH_CONTRAST_COLOR = '#929496';
export const PP_LIGHT_GRAY_COLOR = '#545D68';
export const PP_DIVIDER_COLOR = '#F1EDE8';
export const PP_FOCUS_COLOR = '#0070E0';
export const PP_FOCUS_OUTLINE_COLOR = '#D1E5F9';
export const PP_SHIMMER_COLOR = '#E6E0D9';
export const PP_SHIMMER_INVERSE_COLOR = '#545D68';
export const PP_SHEEN_COLOR = '#FFFFFF';
export const PP_SHADOW_COLOR = '#000000';
export const PP_SCRIM_COLOR = 'rgba(0, 0, 0, 0.4)';
export const PP_ERROR_OUTLINE_COLOR = 'rgba(224, 45, 80, 0.16)';
export const PP_BORDER_LOW_CONTRAST_COLOR = 'rgba(0, 0, 0, 0.1)';
export const PP_BOX_SHADOW_COLOR = 'rgba(0, 0, 0, 0.15)';

// 1.0 COLORS [CONTROL COLORS]
// BRAND PRIMARY MAIN COLORS
export const CONTROL_PRIMARY_COLOR = '#0070BA';
export const CONTROL_SECONDARY_COLOR = '#005EA6';

// TEXT COLORS
export const CONTROL_PRIMARY_TEXT_COLOR = '#2C2E2F';
export const CONTROL_SUPPORTING_TEXT_COLOR = '#687173';
export const CONTROL_FORM_TEXT_COLOR = '#000000';
export const CONTROL_LINK_TEXT_COLOR = '#0070BA';
export const CONTROL_BORDER_HIGH_CONTRAST_COLOR = '#909697';
export const CONTROL_TERMS_COLOR = '#6c7378';

// BLUE TONES
export const CONTROL_COLOR_BLUE_100 = '#F5FBFE';
export const CONTROL_COLOR_BLUE_200 = '#e4f1fb';
export const CONTROL_COLOR_BLUE_300 = '#bfdbee';
export const CONTROL_COLOR_BLUE_400 = '#50c7f9';
export const CONTROL_COLOR_BLUE_500 = '#009cde';
export const CONTROL_COLOR_BLUE_600 = '#0070ba';
export const CONTROL_COLOR_BLUE_700 = '#005ea6';
export const CONTROL_COLOR_BLUE_800 = '#003087';

// GREY TONES
export const CONTROL_COLOR_GREY_100 = '#f5f7fa';
export const CONTROL_COLOR_GREY_200 = '#eaeced';
export const CONTROL_COLOR_GREY_300 = '#cbd2d6';
export const CONTROL_COLOR_GREY_400 = '#b7bcbf';
export const CONTROL_COLOR_GREY_500 = '#9da3a6';
export const CONTROL_COLOR_GREY_600 = '#687173';
export const CONTROL_COLOR_GREY_700 = '#2c2e2f';

// GREEN TONES
export const CONTROL_COLOR_GREEN_100 = '#f5fdfb';
export const CONTROL_COLOR_GREEN_500 = '#00cf92';
export const CONTROL_COLOR_GREEN_600 = '#299976';
export const CONTROL_COLOR_GREEN_400 = '#018065';

// RED TONES
export const CONTROL_COLOR_RED_100 = '#fff7f7';
export const CONTROL_COLOR_RED_500 = '#d20000';

// ORANGE TONES
export const CONTROL_COLOR_ORANGE_100 = '#fffbf5';
export const CONTROL_COLOR_ORANGE_300 = '#ffbd5d';
export const CONTROL_COLOR_ORANGE_500 = '#ff9600';
export const CONTROL_COLOR_ORANGE_700 = '#d64003';

// PURPLE COLOR
export const CONTROL_COLOR_PURPLE_500 = '#640487';

// PINK COLOR
export const CONTROL_COLOR_PINK_500 = '#de0063';
/************ THEME COLORS END ***************/

// Fonts//
export const PAYPAL_HELVETICA_FONT = `
"Helvetica Neue",
"HelveticaNeue",
"HelveticaNeue-Light",
"Helvetica Neue Light",
helvetica,
arial,
Roboto,
sans-serif
`;

export const PAYPAL_OPEN_FONT = `
"PayPal Open",
  "Helvetica Neue",
  "HelveticaNeue",
  "HelveticaNeue-Light",
  "Helvetica Neue Light",
  helvetica,
  arial,
  Roboto,
  sans-serif`;
/* ----- BEGIN AB Testing Variables ----- */

// FONT SIZE
export const FONT_SIZE_X_LARGE = '28px';
export const FONT_SIZE_LARGE = '24px';
export const FONT_SIZE_MEDIUM = '22px';
export const FONT_SIZE_REGULAR = '18px';
export const FONT_SIZE_SMALL = '14px';

// FONT WEIGHT
export const FONT_WEIGHT_NORMAL_PP = 400;
export const FONT_WEIGHT_BOLD_PP = 700;

// MARGIN
export const ADDRESS_MARGIN_BOTTOM_PP = '16px';

//LINK STATES
export const LINK_HOVER_BOTTOM_PPREACT = `1px solid ${BUTTON_FOCUSED_COLOR}`;
export const LINK_HOVER_OPACITY_PPREACT = '1';
/* ----- END AB Testing Variables ----- */
